import { useMemo } from 'react'
import type {
  Document,
  IDocumentsStepContext,
  IFixedAssetDepreciationStepContext,
  Receipt,
} from '../../end-of-year-review/types'

interface ReuploadStatus {
  requestReUpload: boolean
  adminNote?: string
  updatedByUser?: boolean
}

/**
 * Pure function to find a document's re-upload status.
 * Use this in loops/maps where hooks can't be called.
 *
 * @param stepContext - The step context containing documents array
 * @param categoryCode - The document category code to match
 * @param provider - The provider to match
 * @param isFollowUp - Whether we're in follow-up mode
 * @param documentName - Optional document name for more specific matching
 * @returns The matching document or undefined
 */
export function getDocumentReuploadStatus(
  stepContext: IDocumentsStepContext | undefined,
  categoryCode: string,
  provider: string,
  isFollowUp: boolean,
  documentName?: string
): Document | undefined {
  if (!isFollowUp) return undefined

  return stepContext?.documents?.find((doc) => {
    const matchesBase =
      doc.categoryCode === categoryCode && doc.provider === provider
    if (documentName) {
      return matchesBase && doc.name === documentName
    }
    return matchesBase
  })
}

/**
 * Hook to get re-upload status for a specific document type.
 * Used by document type components to determine if a re-upload is needed
 * and what admin note to display.
 *
 * @param stepContext - The step context containing documents array
 * @param categoryCode - The document category code to match
 * @param provider - The provider to match
 * @param isFollowUp - Whether we're in follow-up mode
 * @param documentName - Optional document name for more specific matching (e.g., processing fees)
 * @returns The matching document or undefined if not in follow-up mode or no match
 */
export function useDocumentReuploadStatus(
  stepContext: IDocumentsStepContext | undefined,
  categoryCode: string,
  provider: string,
  isFollowUp: boolean,
  documentName?: string
): Document | undefined {
  return useMemo(() => {
    if (!isFollowUp) return undefined

    return stepContext?.documents?.find((doc) => {
      const matchesBase =
        doc.categoryCode === categoryCode && doc.provider === provider
      if (documentName) {
        return matchesBase && doc.name === documentName
      }
      return matchesBase
    })
  }, [stepContext?.documents, categoryCode, provider, isFollowUp, documentName])
}

/**
 * Hook to get aggregated re-upload status for receipts.
 * Used by asset-depreciation-document where multiple receipts can share one card.
 * Combines admin notes from all receipts that need re-upload.
 *
 * @param stepContext - The step context containing receipts array
 * @param isFollowUp - Whether we're in follow-up mode
 * @returns Aggregated re-upload status with combined admin notes, or undefined
 */
export function useReceiptReuploadStatus(
  stepContext: IFixedAssetDepreciationStepContext | undefined,
  isFollowUp: boolean
): ReuploadStatus | undefined {
  return useMemo(() => {
    if (!isFollowUp) return undefined

    const receipts = stepContext?.receipts ?? []
    const reuploadReceipts = receipts.filter(
      (r): r is Receipt & { requestReUpload: true } =>
        r.requestReUpload === true
    )

    if (reuploadReceipts.length === 0) return undefined

    // Check if all receipts needing re-upload have been updated by the user
    const allUpdated = reuploadReceipts.every((r) => r.updatedByUser === true)

    const adminNotes = reuploadReceipts
      .map((r) => r.adminNote)
      .filter(Boolean)
      .join('; ')

    return {
      requestReUpload: true,
      adminNote: adminNotes || undefined,
      updatedByUser: allUpdated,
    }
  }, [stepContext?.receipts, isFollowUp])
}
